import type { ReactNode } from 'react';
import { Box, Flex, Image, Link, Text } from '@/ui';
import mascot from './assets/mascot.png';

// The marketing homepage's `datesWash`, copied verbatim so the two read as the
// same launch: blue at the top easing into magenta, with a blue radial in the
// top left that reaches zero on its own, all over a near-white paper base. The
// navy-tinted ink is deliberately not `ink.strong` (a neutral #1A1A1A) - against
// this wash a warm-neutral black goes muddy.
const PAPER = '#F4F4FC';
const BACKDROP = `radial-gradient(90% 62% at 12% -8%,
    rgba(143, 182, 246, 0.30) 0%, rgba(143, 182, 246, 0.12) 44%,
    rgba(143, 182, 246, 0) 76%),
  linear-gradient(180deg,
    rgba(143, 182, 246, 0.30) 0%,
    rgba(143, 182, 246, 0.17) 20%,
    rgba(176, 120, 190, 0.13) 44%,
    rgba(196, 41, 138, 0.10) 66%,
    rgba(196, 41, 138, 0.05) 84%,
    rgba(196, 41, 138, 0) 100%)`;
const INK = '#14172A';
const SLATE = '#4E5470';

type Props = {
  children: ReactNode;
};

/**
 * The auth page as a half-and-half split, used when someone arrives from the
 * dates.new domain and would otherwise land on a bare sign-in form. Mirrors the
 * marketing site's hero so the handoff from dates.new into the product doesn't
 * change character mid-flow.
 *
 * The tinted half is a fixed 50vw panel rather than a sized column: this page
 * renders inside AuthLayout's padded, max-width container (shared with
 * reset-password), and a flowed element cannot reach the viewport edges from in
 * there. The columns below are each `flex="1"` inside that centred container, so
 * their midpoints line up with the two viewport halves regardless.
 */
export const DatesSplit = ({ children }: Props) => (
  <Flex align="center" direction={{ base: 'column', lg: 'row' }} w="100%">
    <Box
      aria-hidden
      bgColor={PAPER}
      bgImage={BACKDROP}
      bgPosition="top center"
      bgRepeat="no-repeat"
      bgSize="100% 150vh"
      bottom={0}
      display={{ base: 'none', lg: 'block' }}
      left={0}
      position="fixed"
      top={0}
      w="50vw"
      zIndex={0}
    />

    <Flex
      align="center"
      direction="column"
      display={{ base: 'none', lg: 'flex' }}
      flex="1"
      position="relative"
      px="8"
      zIndex={1}
    >
      <Image alt="" aria-hidden src={mascot} w={{ lg: '150px', xl: '165px' }} />

      {/* One sentence broken across three lines - kept in a single element so it
          is announced as "Say hello to Dates by Agenda Hero" rather than as three
          stray fragments. Deliberately not a heading: the form's own h2 is the
          page heading, and this half disappears below lg. */}
      <Text as="p" mt="5" textAlign="center">
        <Text
          as="span"
          color={SLATE}
          display="block"
          fontFamily="display"
          fontSize={{ lg: '25px', xl: '27px' }}
          lineHeight="1"
        >
          Say hello to
        </Text>
        <Text
          as="span"
          color={INK}
          display="block"
          fontFamily="display"
          fontSize={{ lg: '120px', xl: '132px' }}
          letterSpacing="-0.015em"
          lineHeight="1"
          mt="4"
        >
          Dates
        </Text>
        <Text
          as="span"
          color={SLATE}
          display="block"
          fontSize={{ lg: '16px', xl: '17px' }}
          fontWeight={600}
          mt="5"
        >
          by Agenda Hero
        </Text>
      </Text>

      {/* A plain href, not a RouterLink: the marketing homepage is served by the
          proxy, while the SPA's own "/" route redirects to /magic. A client-side
          navigation would never reach it. */}
      <Link
        _hover={{ color: INK, textDecorationColor: INK }}
        color={SLATE}
        fontSize={{ lg: '15px', xl: '16px' }}
        fontWeight={600}
        href="/"
        mt="10"
        textDecoration="underline"
        textDecorationThickness="1.5px"
        textUnderlineOffset="4px"
      >
        Learn more
        <Box aria-hidden as="span" ml="1.5">
          &rarr;
        </Box>
      </Link>
    </Flex>

    <Flex
      align="center"
      flex="1"
      justify="center"
      position="relative"
      zIndex={1}
    >
      {children}
    </Flex>
  </Flex>
);
